import React, { useCallback } from "react";
import { FiEdit2, FiEye, FiTrash2, FiCheckCircle, FiBookOpen, FiPaperclip, FiCopy } from "react-icons/fi";
import {
  ClarificationStatus,
  type Clarification,
  type UpdateClarificationPayload,
} from "../../types/clarification.type";
import { formattedSimpleDate } from "utils/dateFormat";
import { ClarificationStatusBadge } from "../ClarificationBadges";
import { ClarificationRowActionButton } from "./ClarificationRowActionButton";
import { ClarificationChatButton } from "../ClarificationChat";
import FallbackImage from "newui/components/FallbackImage/FallbackImage";
import { formatClarificationRef } from "../../utils/formatClarificationRef";

interface ClarificationRowProps {
  clarification: Clarification;
  hasSelectableItems: boolean;
  isClientView: boolean;
  isSelected?: boolean;
  showSupplierColumn?: boolean;
  unreadCount?: number;
  onEdit: (clarification: Clarification) => void;
  onView: (clarification: Clarification) => void;
  onCopyToSuppliers?: (clarification: Clarification) => void;
  onDelete?: (clarification: Clarification) => void;
  onInlineEdit?: (id: number, payload: UpdateClarificationPayload) => Promise<boolean>;
  onOpenChat?: (clarification: Clarification) => void;
  onRowClick?: () => void;
  onSupplierNameClick?: (submissionId: number) => void;
  onToggleSelect?: () => void;
}

export const ClarificationRow: React.FC<ClarificationRowProps> = ({
  clarification,
  hasSelectableItems,
  isClientView,
  isSelected = false,
  showSupplierColumn = false,
  onRowClick,
  onOpenChat,
  onSupplierNameClick,
  onToggleSelect,
  onEdit,
  onView,
  onDelete,
  onCopyToSuppliers,
  onInlineEdit,
  unreadCount = 0,
}) => {

  // Determine if row can be edited inline
  const canEditResponse = !isClientView && clarification.is_sent && clarification.is_draft_response;
  const canDelete = isClientView && !clarification.is_sent;
  const canSelect = (isClientView && !clarification.is_sent) || (!isClientView && clarification.is_draft_response && clarification.final_response);
  const canEdit = isClientView && !clarification.is_sent;

  const handleAction = () => {
    if (isClientView && clarification.status === ClarificationStatus.closed) {
      onView(clarification);
    } else {
      onEdit(clarification);
    }
  };

  const handleToggleComplete = useCallback(async () => {
    if (!onInlineEdit) {
      return;
    }
    await onInlineEdit(clarification.id, {
      is_complete: !clarification.is_complete,
    });
  }, [clarification.id, clarification.is_complete, onInlineEdit]);

  const truncateText = (text: string, maxLength: number) => {
    if (!text) {
      return "-";
    }
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  const clarificationRef = formatClarificationRef(clarification.supplier_name, clarification.reference_number);

  return (
    <tr
      className={`border-b border-gray-200 hover:bg-gray-50 ${!clarification.is_sent ? "bg-blue-50/50" : ""} ${onRowClick ? "cursor-pointer" : ""}`}
      onClick={onRowClick}
    >
      {/* Checkbox column for drafts */}
      {hasSelectableItems && (
        <td className="w-10 px-4 py-3" onClick={(e) => e.stopPropagation()}>
          {canSelect && onToggleSelect && (
            <input
              type="checkbox"
              checked={isSelected}
              onChange={onToggleSelect}
              className="clarification-row-checkbox h-4 w-4 rounded border-gray-300 text-blue-600"
            />
          )}
        </td>
      )}
      <td className="w-10 px-4 py-3 text-sm">
        {clarificationRef}
      </td>
      {showSupplierColumn && (
        <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500" onClick={(e) => onSupplierNameClick && e.stopPropagation()}>
          {clarification.supplier_name ? (
            <span className="inline-flex items-center gap-2" onClick={() => onSupplierNameClick?.(clarification.rfx_submission_id)}>
              <FallbackImage
                src={clarification.supplier_logo || ""}
                alt={clarification.supplier_name}
                className="h-5 w-5 rounded-full object-cover"
              />
              {clarification.supplier_name}
            </span>
          ) : "-"}
        </td>
      )}
      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">
        {clarification.clarification_type?.name || "-"}
      </td>
      {/* Status column */}
      <td className="whitespace-nowrap px-4 py-3 text-center text-sm">
        <ClarificationStatusBadge status={clarification.status} />
      </td>
      <td className="w-[300px] px-4 py-3 text-sm text-gray-900">
        <div className="break-words line-clamp-3" title={clarification.clarification_text} dangerouslySetInnerHTML={{ __html: clarification.clarification_text || "-" }}>
        </div>
      </td>
      <td className="w-[200px] px-4 py-3 text-sm text-gray-500">
        <div className="break-words line-clamp-2">
          {clarification.relates_to || "-"}
        </div>
      </td>
      {/* <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">
        <ClarificationImpactBadge clarification_impact_id={clarification.clarification_impact_id} />
      </td> */}
      {/* Response column - inline editable for suppliers */}
      <td>
        <div className="pl-10">
          {clarification.final_response?.length > 0 && <FiCheckCircle className="stroke-green-700" />}
        </div>
      </td>
      {/* Actions column */}
      <td className="whitespace-nowrap px-4 py-3 text-right text-sm" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-end gap-2">
          {canEdit && (
            <ClarificationRowActionButton icon={FiEdit2} onClick={() => onEdit(clarification)} title="Edit" />
          )}
          {(!isClientView || clarification.is_sent) &&
            <ClarificationRowActionButton icon={canEditResponse ? FiEdit2 : FiEye} onClick={handleAction} title={isClientView || clarification.status === ClarificationStatus.closed ? "View" : "Respond"} />
          }
          {onOpenChat && clarification.is_sent && (
            <ClarificationChatButton
              clarification={clarification}
              onOpenChat={onOpenChat}
              unreadCount={unreadCount}
            />
          )}
          {isClientView && clarification.is_sent &&
            <ClarificationRowActionButton icon={clarification.is_complete ? FiBookOpen : FiCheckCircle} onClick={handleToggleComplete} title={clarification.is_complete ? "Reopen Clarification" : "Mark as Complete"} />
          }
          {onCopyToSuppliers && isClientView && (
            <span data-guide-action={!clarification.is_sent ? "copy-draft" : undefined}>
              <ClarificationRowActionButton icon={FiCopy} onClick={() => onCopyToSuppliers(clarification)} title="Copy to Suppliers" />
            </span>
          )}
          {canDelete && onDelete && (
            <ClarificationRowActionButton icon={FiTrash2} onClick={() => onDelete(clarification)} colorOverride="text-red-600" hoverColorOverride="hover:text-red-800" title="Delete" />
          )}
        </div>
      </td>
    </tr>
  );
};
